import { useState, useCallback, useRef, useEffect } from 'react';
import { GaussianBlurBackgroundProcessor } from '@twilio/video-processors';

/**
 * Hook for managing Twilio Video Processors
 * 
 * Official video track configuration recommendations:
 * - Width: 640px, Height: 480px, Frame Rate: 24fps
 * - Higher resolutions decrease FPS, especially on browsers without SIMD support
 * - Frame rates above 24fps increase CPU usage without noticeable quality improvement
 * 
 * Default blur radius values (from official documentation):
 * - maskBlurRadius: 8 (blur radius for person mask)
 * - blurFilterRadius: 15 (blur radius for background)
 * 
 * @see https://twilio.github.io/twilio-video-processors.js/classes/GaussianBlurBackgroundProcessor.html
 * @see https://twilio.github.io/twilio-video-processors.js/interfaces/GaussianBlurBackgroundProcessorOptions.html
 */
export const useVideoProcessors = () => {
  const [isBackgroundBlurEnabled, setIsBackgroundBlurEnabled] = useState(false);
  const [isProcessorLoading, setIsProcessorLoading] = useState(false);
  const [processorError, setProcessorError] = useState(null);
  const processorRef = useRef(null);
  const currentTrackRef = useRef(null);  // Track which video track has the processor
  const cleanupIntervalRef = useRef(null);  // Periodic cleanup interval
  const performanceMonitorRef = useRef(null);  // Performance monitoring

  // Get the assets path based on environment
  const getAssetsPath = useCallback(() => {
    // Allow override via environment variable (useful for CDN deployment)
    if (process.env.REACT_APP_ASSETS_PATH) {
      return process.env.REACT_APP_ASSETS_PATH;
    }
    
    // For production, use relative path to be served by web server
    if (process.env.NODE_ENV === 'production') {
      // Use absolute path with origin for better reliability
      return `${window.location.origin}/assets`;
    }
    
    // For development, use local Node.js server
    return 'http://localhost:5000/assets';
  }, []);

  // Initialize the background blur processor
  const initializeProcessor = useCallback(async () => {
    try {
      if (!processorRef.current) {
        console.log('Creating GaussianBlurBackgroundProcessor...');
        
        // Check WebAssembly support (required for background blur)
        if (typeof WebAssembly === 'undefined') {
          throw new Error('WebAssembly is not supported in this browser. Background blur is not available.');
        }
        
        // Check if we have sufficient CPU resources (rough estimate)
        const cpuWarning = navigator.hardwareConcurrency && navigator.hardwareConcurrency < 4;
        if (cpuWarning) {
          console.warn('Low CPU core count detected. Background blur may impact performance.');
        }
        
        const assetsPath = getAssetsPath();
        console.log(`Using assets path: ${assetsPath}`);
        
        // Test asset availability before creating processor
        try {
          const testResponse = await fetch(`${assetsPath}/selfie_segmentation_landscape.tflite`);
          if (!testResponse.ok) {
            throw new Error(`Assets not available at ${assetsPath}. Status: ${testResponse.status}`);
          }
          console.log('Assets are available at:', assetsPath);
        } catch (fetchError) {
          console.error('Asset availability check failed:', fetchError);
          throw new Error(`Cannot access background blur assets at ${assetsPath}. Please ensure assets are properly deployed.`);
        }
        
        // Use official API with all constructor options
        const processor = new GaussianBlurBackgroundProcessor({
          assetsPath,
          maskBlurRadius: 8,        // Default from official docs
          blurFilterRadius: 15,     // Default from official docs
          useWebWorker: true,       // Enable web worker for better performance
          deferInputFrameDownscale: false  // Disable to avoid trailing effects
        });
        
        // Validate processor was created successfully
        if (!processor) {
          throw new Error('Failed to create GaussianBlurBackgroundProcessor instance');
        }
        
        console.log('Loading background blur model...');
        
        // Add timeout for model loading to prevent indefinite hanging
        const modelLoadPromise = processor.loadModel();
        const timeoutPromise = new Promise((_, reject) => {
          setTimeout(() => reject(new Error('Model loading timed out after 30 seconds')), 30000);
        });
        
        await Promise.race([modelLoadPromise, timeoutPromise]);
        console.log('Background blur model loaded successfully');
        
        // Validate processor is ready for use (if isLoaded property exists)
        if (processor.isLoaded !== undefined && !processor.isLoaded) {
          throw new Error('Background blur model failed to load properly');
        }
        
        // Additional validation: check if processor has required methods
        if (typeof processor.processFrame !== 'function') {
          throw new Error('Processor is missing required processFrame method');
        }
        
        processorRef.current = processor;
      }
      return processorRef.current;
    } catch (error) {
      console.error('Failed to initialize background blur processor:', error);
      
      // Clean up on error
      processorRef.current = null;
      
      setProcessorError(error.message);
      throw error;
    }
  }, [getAssetsPath]);

  // Performance monitoring for frame processing
  const startPerformanceMonitoring = useCallback(() => {
    if (performanceMonitorRef.current) {
      clearInterval(performanceMonitorRef.current);
    }
    
    let frameCount = 0;
    let totalProcessingTime = 0;
    let maxProcessingTime = 0;
    let slowFrameCount = 0;
    
    performanceMonitorRef.current = setInterval(() => {
      if (frameCount > 0) {
        const avgProcessingTime = totalProcessingTime / frameCount;
        const slowFramePercentage = (slowFrameCount / frameCount) * 100;
        
        console.debug('Background blur performance stats:', {
          frames: frameCount,
          avgProcessingTime: Math.round(avgProcessingTime * 100) / 100 + 'ms',
          maxProcessingTime: Math.round(maxProcessingTime * 100) / 100 + 'ms',
          slowFramePercentage: Math.round(slowFramePercentage) + '%'
        });
        
        // Warn if performance is poor
        if (avgProcessingTime > 33 || slowFramePercentage > 10) {
          console.warn('Background blur performance degraded:', {
            avgProcessingTime: avgProcessingTime + 'ms',
            slowFramePercentage: slowFramePercentage + '%',
            recommendation: 'Consider disabling background blur or reducing video quality'
          });
        }
        
        // Reset counters
        frameCount = 0;
        totalProcessingTime = 0;
        maxProcessingTime = 0;
        slowFrameCount = 0;
      }
    }, 10000); // Every 10 seconds
  }, []);

  const stopPerformanceMonitoring = useCallback(() => {
    if (performanceMonitorRef.current) {
      clearInterval(performanceMonitorRef.current);
      performanceMonitorRef.current = null;
    }
  }, []);

  // Periodic cleanup to prevent VideoFrame accumulation
  const startPeriodicCleanup = useCallback(() => {
    if (cleanupIntervalRef.current) {
      clearInterval(cleanupIntervalRef.current);
    }
    
    cleanupIntervalRef.current = setInterval(() => {
      // Force garbage collection if available (Chrome DevTools)
      if (window.gc) {
        window.gc();
      }
      
      // Log VideoFrame count for debugging (if available)
      if (window.performance && window.performance.memory) {
        const memory = window.performance.memory;
        console.debug('Memory usage:', {
          used: Math.round(memory.usedJSHeapSize / 1024 / 1024) + 'MB',
          total: Math.round(memory.totalJSHeapSize / 1024 / 1024) + 'MB'
        });
      }
    }, 5000); // Every 5 seconds
  }, []);

  const stopPeriodicCleanup = useCallback(() => {
    if (cleanupIntervalRef.current) {
      clearInterval(cleanupIntervalRef.current);
      cleanupIntervalRef.current = null;
    }
  }, []);

  const stopAllMonitoring = useCallback(() => {
    stopPeriodicCleanup();
    stopPerformanceMonitoring();
  }, [stopPeriodicCleanup, stopPerformanceMonitoring]);

  // Clean up processor from current track
  const cleanupCurrentProcessor = useCallback(async () => {
    if (processorRef.current && currentTrackRef.current) {
      try {
        console.log('Cleaning up processor from current track...');
        await currentTrackRef.current.removeProcessor(processorRef.current);
        currentTrackRef.current = null;
        
        // Stop all monitoring when processor is removed
        stopAllMonitoring();
        
        // Force garbage collection for VideoFrames
        if (window.gc) {
          window.gc();
        }
      } catch (error) {
        console.warn('Error removing processor from track:', error);
      }
    }
  }, [stopAllMonitoring]);

  // Add background blur to a video track
  const addBackgroundBlur = useCallback(async (videoTrack) => {
    if (!videoTrack) {
      throw new Error('No video track provided');
    }

    // Check if the video track is in a valid state
    if (!videoTrack.mediaStreamTrack || videoTrack.mediaStreamTrack.readyState !== 'live') {
      throw new Error('Video track is not in a valid state');
    }

    try {
      setIsProcessorLoading(true);
      setProcessorError(null);

      // Clean up any existing processor first to prevent VideoFrame leaks
      await cleanupCurrentProcessor();

      console.log('Initializing processor for background blur...');
      const processor = await initializeProcessor();
      
      // Validate processor is ready
      if (!processor) {
        throw new Error('Failed to initialize background blur processor');
      }
      
      console.log('Adding processor to video track...');
      // Use 2d context type for better compatibility (fixes Canvas2D context null errors)
      await videoTrack.addProcessor(processor, {
        inputFrameBufferType: 'videoframe',
        outputFrameBufferContextType: '2d'
      });
      
      // Store reference to current track for cleanup
      currentTrackRef.current = videoTrack;
      
      // Start monitoring systems
      startPeriodicCleanup();
      startPerformanceMonitoring();
      
      // Small delay to ensure processor is fully initialized
      await new Promise(resolve => setTimeout(resolve, 100));
      
      console.log('Background blur enabled successfully');
      setIsBackgroundBlurEnabled(true);
      return videoTrack;
    } catch (error) {
      console.error('Failed to add background blur:', error);
      
      // Clean up on error
      currentTrackRef.current = null;
      setIsBackgroundBlurEnabled(false);
      
      setProcessorError(error.message);
      throw error;
    } finally {
      setIsProcessorLoading(false);
    }
  }, [initializeProcessor, cleanupCurrentProcessor, startPeriodicCleanup, startPerformanceMonitoring]);

  // Remove background blur from a video track
  const removeBackgroundBlur = useCallback(async (videoTrack) => {
    if (!videoTrack) {
      throw new Error('No video track provided');
    }

    try {
      setIsProcessorLoading(true);
      setProcessorError(null);

      // Remove the processor from the video track
      if (processorRef.current) {
        console.log('Removing processor from video track...');
        await videoTrack.removeProcessor(processorRef.current);
        currentTrackRef.current = null;
        console.log('Background blur disabled successfully');
      } else {
        console.log('No processor to remove');
      }
      
      setIsBackgroundBlurEnabled(false);
      return videoTrack;
    } catch (error) {
      console.error('Failed to remove background blur:', error);
      setProcessorError(error.message);
      throw error;
    } finally {
      setIsProcessorLoading(false);
    }
  }, []);

  // Toggle background blur on/off
  const toggleBackgroundBlur = useCallback(async (videoTrack) => {
    if (isBackgroundBlurEnabled) {
      return await removeBackgroundBlur(videoTrack);
    } else {
      return await addBackgroundBlur(videoTrack);
    }
  }, [isBackgroundBlurEnabled, addBackgroundBlur, removeBackgroundBlur]);

  // Update blur settings (can be called while processor is active)
  const updateBlurSettings = useCallback(({ maskBlurRadius, blurFilterRadius }) => {
    if (processorRef.current) {
      if (maskBlurRadius !== undefined) {
        processorRef.current.maskBlurRadius = maskBlurRadius;
        console.log(`Updated maskBlurRadius to ${maskBlurRadius}`);
      }
      if (blurFilterRadius !== undefined) {
        processorRef.current.blurFilterRadius = blurFilterRadius;
        console.log(`Updated blurFilterRadius to ${blurFilterRadius}`);
      }
    } else {
      console.warn('No processor available to update blur settings');
    }
  }, []);

  // Cleanup processor with proper disposal
  const cleanupProcessor = useCallback(async () => {
    console.log('Cleaning up background blur processor...');
    
    try {
      // Stop all monitoring
      stopAllMonitoring();
      
      // First remove processor from current track
      await cleanupCurrentProcessor();
      
      // Then dispose of the processor
      if (processorRef.current) {
        try {
          // Call dispose if the processor has this method
          if (typeof processorRef.current.dispose === 'function') {
            processorRef.current.dispose();
          }
          
          // Additional cleanup for WebWorker if used
          if (processorRef.current.worker) {
            processorRef.current.worker.terminate();
          }
        } catch (error) {
          console.warn('Error disposing processor:', error);
        }
        
        // Clear the reference
        processorRef.current = null;
      }
      
      // Clear track reference
      currentTrackRef.current = null;
      
      // Force garbage collection for VideoFrames
      if (window.gc) {
        window.gc();
      }
    } catch (error) {
      console.warn('Error during processor cleanup:', error);
    }
    
    setIsBackgroundBlurEnabled(false);
    setProcessorError(null);
  }, [cleanupCurrentProcessor, stopAllMonitoring]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopAllMonitoring();
    };
  }, [stopAllMonitoring]);

  return {
    isBackgroundBlurEnabled,
    isProcessorLoading,
    processorError,
    addBackgroundBlur,
    removeBackgroundBlur,
    toggleBackgroundBlur,
    updateBlurSettings,
    cleanupProcessor
  };
}; 